import { Controller } from "@hotwired/stimulus"

// Client-side filter for the consolidated "My Library" tab on the public profile.
// Handles four dimensions: kind chip (book/pamphlet/...), free-text search,
// and toggleable tag + category chips. An item must match ALL active filters
// to remain visible.
export default class extends Controller {
  static targets = [
    "chip", "item", "grid", "search", "emptyState",
    "advancedToggle", "advancedPanel", "advancedChevron",
    "tagChip", "categoryChip"
  ]

  connect() {
    this.activeKind = "all"
    this.query = ""
    this.activeTags = new Set()
    this.activeCategories = new Set()
  }

  setKind(event) {
    this.activeKind = event.currentTarget.dataset.kind || "all"
    this.refreshKindChips()
    this.applyFilter()
  }

  setQuery(event) {
    this.query = (event.target.value || "").trim().toLowerCase()
    this.applyFilter()
  }

  toggleTag(event) {
    const tag = event.currentTarget.dataset.tag
    if (!tag) return
    this.activeTags.has(tag) ? this.activeTags.delete(tag) : this.activeTags.add(tag)
    this.refreshAdvancedChips()
    this.applyFilter()
  }

  toggleCategory(event) {
    const cat = event.currentTarget.dataset.category
    if (!cat) return
    this.activeCategories.has(cat) ? this.activeCategories.delete(cat) : this.activeCategories.add(cat)
    this.refreshAdvancedChips()
    this.applyFilter()
  }

  clearAdvanced() {
    this.activeTags.clear()
    this.activeCategories.clear()
    this.refreshAdvancedChips()
    this.applyFilter()
  }

  toggleAdvanced() {
    if (!this.hasAdvancedPanelTarget) return
    const isHidden = this.advancedPanelTarget.classList.toggle("hidden")
    this.advancedToggleTarget.setAttribute("aria-expanded", String(!isHidden))
    if (this.hasAdvancedChevronTarget) {
      this.advancedChevronTarget.classList.toggle("rotate-180", !isHidden)
    }
  }

  refreshKindChips() {
    this.chipTargets.forEach((chip) => {
      const isActive = chip.dataset.kind === this.activeKind
      chip.classList.toggle("bg-beige-800", isActive)
      chip.classList.toggle("text-white", isActive)
      chip.classList.toggle("border-beige-800", isActive)
      chip.classList.toggle("bg-white", !isActive)
      chip.classList.toggle("text-beige-700", !isActive)
      chip.classList.toggle("border-beige-200", !isActive)
      chip.classList.toggle("hover:bg-beige-50", !isActive)
    })
  }

  refreshAdvancedChips() {
    this.tagChipTargets.forEach((chip) => {
      const isActive = this.activeTags.has(chip.dataset.tag)
      // Active tag → purple-filled; inactive → purple outline
      chip.classList.toggle("bg-purple-700", isActive)
      chip.classList.toggle("text-white", isActive)
      chip.classList.toggle("border-purple-700", isActive)
      chip.classList.toggle("bg-purple-50", !isActive)
      chip.classList.toggle("text-purple-800", !isActive)
      chip.classList.toggle("border-purple-200", !isActive)
    })
    this.categoryChipTargets.forEach((chip) => {
      const isActive = this.activeCategories.has(chip.dataset.category)
      chip.classList.toggle("bg-amber-700", isActive)
      chip.classList.toggle("text-white", isActive)
      chip.classList.toggle("border-amber-700", isActive)
      chip.classList.toggle("bg-amber-50", !isActive)
      chip.classList.toggle("text-amber-800", !isActive)
      chip.classList.toggle("border-amber-200", !isActive)
    })
  }

  applyFilter() {
    let visibleCount = 0
    this.itemTargets.forEach((item) => {
      const kindMatch = this.activeKind === "all" || item.dataset.kind === this.activeKind
      const nameMatch = this.query.length === 0 || (item.dataset.name || "").includes(this.query)

      const itemTags = (item.dataset.tags || "").split("|").filter(Boolean)
      const tagMatch = this.activeTags.size === 0 || [ ...this.activeTags ].every((t) => itemTags.includes(t))

      const itemCats = (item.dataset.categories || "").split("|").filter(Boolean)
      const catMatch = this.activeCategories.size === 0 || [ ...this.activeCategories ].every((c) => itemCats.includes(c))

      const visible = kindMatch && nameMatch && tagMatch && catMatch
      item.classList.toggle("hidden", !visible)
      if (visible) visibleCount += 1
    })
    if (this.hasEmptyStateTarget) {
      this.emptyStateTarget.classList.toggle("hidden", visibleCount > 0)
    }
  }
}
